import * as Yup from 'yup';
import { FormikValues } from 'formik';
import { Utils, Client } from '@gv/triage-components';

import {
  CreateClientPayload,
  UpdateClientPayload,
  CommonClientPayload,
  CommonClientPayloadV2,
} from 'store/api/clients';
import {
  removeFields,
  getClientPhonesPayload,
  removeEmptyStringsInArray,
  getClientPhonesInitialValues,
  removeEmptyStringsInObjectArrays,
} from 'utils/helpers';

import {
  getPetPayload,
  petFieldsValidation,
  bitwerxPetFieldsValidation,
  initialValues as newPetInitialValues,
} from '../pet/config';

import { handleFieldArray } from './helpers';

const {
  Validation: { phoneNumber },
  Object: { filterEmptyFields, replaceFieldsValue },
} = Utils;

const emailValidation = Yup.string().trim().email('Email must be a valid');

export const validationSchema = (
  isEdit = false,
  isEmailRequired = false,
  isZipCodeRequired = false,
  isBitwerxEnabled = false
) =>
  Yup.object().shape({
    last_name: Yup.string().trim().required(),
    first_name: Yup.string().trim().required(),
    phones: Yup.array().of(phoneNumber('phone')),
    home_phones: Yup.array().of(phoneNumber('phone')),
    city: isBitwerxEnabled ? Yup.string().trim().required() : Yup.string(),
    state: isBitwerxEnabled ? Yup.string().trim().required() : Yup.string(),
    address: isBitwerxEnabled ? Yup.string().trim().required() : Yup.string(),
    emails:
      isEmailRequired || isBitwerxEnabled
        ? Yup.array().of(emailValidation.required('Email is a required field'))
        : Yup.array().of(emailValidation),
    zip_code:
      isZipCodeRequired || isBitwerxEnabled
        ? Yup.string()
            .trim()
            .min(4, 'ZIP code must be at least 4 characters')
            .max(10, 'ZIP code cannot exceed 10 characters')
            .required()
        : Yup.string()
            .trim()
            .min(4, 'ZIP code must be at least 4 characters')
            .max(10, 'ZIP code cannot exceed 10 characters'),
    ...(isEdit
      ? {}
      : isBitwerxEnabled
        ? bitwerxPetFieldsValidation
        : petFieldsValidation),
  });

export const getPayload = (
  v: FormikValues,
  id: string
): CreateClientPayload => {
  const payload = v;

  const phonesPayload = getClientPhonesPayload(payload);

  payload.hospital_id = Number(id);
  payload.addresses = removeEmptyStringsInArray(payload.addresses);
  payload.emails = removeEmptyStringsInArray(payload.emails);
  payload.email = payload.emails[0];
  payload.pet = getPetPayload(v);

  const filteredPayload = removeFields<FormikValues>(payload, [
    'name',
    'days',
    'years',
    'breed',
    'months',
    'gender',
    'species',
    'pet_name',
    'otherBreed',
  ]);

  return filterEmptyFields({
    ...filteredPayload,
    ...phonesPayload,
  }) as CreateClientPayload;
};

export const getPayloadV2 = (values: FormikValues): CommonClientPayloadV2 => {
  const emails = removeEmptyStringsInArray(values.emails);
  const addresses = removeEmptyStringsInArray(values.addresses);
  const firstName = values.first_name.trim();
  const lastName = values.last_name.trim();
  const { phones, home_phones } = getClientPhonesPayload(values);
  const payload: CommonClientPayloadV2 = {
    emails,
    phones,
    addresses,
    home_phones,
    email: emails[0],
    city: values.city,
    last_name: lastName,
    first_name: firstName,
    address: values.address,
    timezone: values.timezone,
    pets: [getPetPayload(values)],
  };

  return filterEmptyFields<CommonClientPayloadV2>(payload);
};

export const getUpdatePayload = (
  v: FormikValues,
  clientId: string
): UpdateClientPayload => {
  const payload = { ...v };

  const phonesPayload = getClientPhonesPayload(payload);

  payload.client_id = Number(clientId);
  payload.email = payload.emails[0];

  const filteredPayload = removeEmptyStringsInObjectArrays({
    ...payload,
    ...phonesPayload,
  });

  return replaceFieldsValue(filteredPayload, {
    value: '',
    replacer: null,
  }) as UpdateClientPayload;
};

export const getInitialValues = (data?: Client): CommonClientPayload => {
  const [firstName, lastName] = data?.name ? data?.name?.split(' ') : ['', ''];

  const { phones, home_phones } = getClientPhonesInitialValues(data);

  return {
    city: data?.city || '',
    email: data?.email || '',
    state: data?.state || '',
    address: data?.address || '',
    zip_code: data?.zip_code || '',
    timezone: data?.timezone || '',
    phones: handleFieldArray(phones),
    emails: handleFieldArray(data?.emails),
    home_phones: handleFieldArray(home_phones),
    last_name: data?.last_name || lastName || '',
    first_name: data?.first_name || firstName || '',
    addresses: data?.addresses?.length ? [...data.addresses] : [],
  };
};

export const createInitialValues = (
  initialValues?: FormikValues
): CommonClientPayload => ({
  city: '',
  email: '',
  state: '',
  address: '',
  emails: [''],
  zip_code: '',
  phones: [''],
  timezone: '',
  last_name: '',
  addresses: [],
  first_name: '',
  home_phones: [''],
  ...newPetInitialValues,
  ...initialValues,
});
